import { useEffect, useId, useState, type MouseEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Search } from "lucide-react";
import type { VoceElenco } from "@/api/preventivo";
import { euro, COLORE_TONO, tono } from "@/lib/preventivoFormato";
import { dataBreve } from "@/lib/linkPreventivo";
import { AvvisoErrore } from "./BolletteFuori";
import { COLLEGAMENTO_NON_RIUSCITO, COL_DIFFERENZA } from "./scritte";
import { FOCUS } from "./parti";
import {
  CERCA_AMMINISTRATORE, COL_AMMINISTRATORE, COL_CONDOMINI, COL_MODIFICATO, COL_ULTIMO_INVIO, NESSUNA_OFFERTA_SCELTA,
  NESSUN_PREVENTIVO, NON_INDICATO, SENZA_NOME_IN_LISTA, cellaConteggio, cellaInvio, nessunAmministratore, rigaConteggio, rigaDate,
} from "./scritteLista";

/** Da `md:` (768 px) la lista è una tabella; sotto, schede a tutta riga. Senza `matchMedia` (prove) sono schede. */
function useSchermoLargo(): boolean {
  const query = "(min-width: 768px)";
  const [largo, setLargo] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const m = window.matchMedia?.(query);
    if (!m) return;
    const cambia = () => setLargo(m.matches);
    cambia();
    m.addEventListener?.("change", cambia);
    return () => m.removeEventListener?.("change", cambia);
  }, []);
  return largo;
}

/** «Cerca amministratore»: etichetta visibile (form-labels), il valore lo tiene chi lo usa. */
export function CampoCerca({ valore, onCambia }: { valore: string; onCambia: (v: string) => void }) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-text">{CERCA_AMMINISTRATORE}</label>
      <div className="relative md:max-w-sm">
        <Search size={16} aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          id={id}
          type="search"
          autoComplete="off"
          value={valore}
          onChange={(e) => onCambia(e.target.value)}
          className={`h-11 w-full rounded-lg border border-border bg-card pl-9 pr-3 text-base text-text ${FOCUS}`}
        />
      </div>
    </div>
  );
}

interface Props {
  /** `null` finché l'elenco non è arrivato. */
  voci: VoceElenco[] | null;
  /** La ricerca in corso: decide quale stato vuoto si scrive. */
  q: string;
  errore?: boolean;
  onRiprova?: () => void;
  /** Dove porta una riga. */
  href: (v: VoceElenco) => string;
  /** Una riga che non si può scegliere: la frase che lo dice. La riga resta, senza link. */
  spenta?: (v: VoceElenco) => string | undefined;
  /** Un tocco su una riga attiva (prima della navigazione: chi lo riceve può fermarla con `preventDefault`). */
  onApri?: (e: MouseEvent<HTMLAnchorElement>) => void;
  /** Solo schede, anche su schermo largo (la scelta dei file di WhatsApp). */
  schede?: boolean;
  /** Con la lista vuota e nessuna ricerca: il tasto accanto a «Nessun preventivo salvato». */
  azioneVuota?: ReactNode;
}

const RIGA = `flex min-h-16 w-full items-center gap-3 px-4 py-3 touch-manipulation`;

/** Il nome della riga: senza nome si dice con le parole, non solo col corsivo (color-not-only). */
function Nome({ v, className = "" }: { v: VoceElenco; className?: string }) {
  return v.amministratore
    ? <span className={`break-words font-semibold text-text ${className}`}>{v.amministratore}</span>
    : <span className={`break-words font-semibold italic text-muted-foreground ${className}`}>{NON_INDICATO}</span>;
}

/** Sotto il nome: «7 condomìni · 16 forniture»; senza nome, i primi condomìni al posto dei numeri. */
function sotto(v: VoceElenco): string {
  return !v.amministratore && v.primi_condomini.length > 0 ? v.primi_condomini.join(", ") : rigaConteggio(v.condomini, v.forniture);
}

/** Il nome per chi non vede: con un nome, «nome, conteggi»; senza, «Amministratore non indicato» e ciò che si legge sotto. */
function nomeAccessibile(v: VoceElenco): string {
  return v.amministratore ? `${v.amministratore}, ${rigaConteggio(v.condomini, v.forniture)}` : `${NON_INDICATO}, ${sotto(v)}`;
}

function Scheda({ v, href, frase, onApri }: { v: VoceElenco; href: string; frase?: string; onApri?: Props["onApri"] }) {
  const righe = (
    <>
      <Nome v={v} className="block" />
      <span className="block text-sm text-muted-foreground line-clamp-2">{sotto(v)}</span>
      <span className="mt-0.5 block text-xs text-muted-foreground">{rigaDate(v.modificato_il, v.ultimo_invio)}</span>
    </>
  );
  // riga spenta: si attenuano il nome e ciò che sta sotto, non la frase del massimo (resta a contrasto pieno)
  if (frase) {
    return (
      <li>
        <div aria-disabled="true" className={RIGA}>
          <div className="min-w-0 flex-1">
            <div className="opacity-60">{righe}</div>
            <span className="mt-1 block text-xs font-medium text-text">{frase}</span>
          </div>
        </div>
      </li>
    );
  }
  return (
    <li>
      <Link to={href} onClick={onApri} aria-label={nomeAccessibile(v)}
        className={`${RIGA} hover:bg-border/40 ${FOCUS} focus-visible:ring-inset`}>
        <div className="min-w-0 flex-1">{righe}</div>
        <ChevronRight size={20} aria-hidden="true" className="shrink-0 text-muted-foreground" />
      </Link>
    </li>
  );
}

const TH = "px-4 py-2.5 text-xs font-semibold uppercase tracking-wide";

/** Ogni cella è un link: tutta la riga si tocca; solo la prima è nell'albero dell'accessibilità (le altre ripetono). */
function Riga({ v, href, onApri }: { v: VoceElenco; href: string; onApri?: Props["onApri"] }) {
  const cella = (corpo: ReactNode, i: number, extra = "") => (
    <td className={`p-0 ${extra}`}>
      <Link to={href} onClick={onApri} tabIndex={i === 0 ? undefined : -1} aria-hidden={i === 0 ? undefined : true}
        aria-label={i === 0 ? nomeAccessibile(v) : undefined}
        className={`block px-4 py-3 ${FOCUS} focus-visible:ring-inset`}>{corpo}</Link>
    </td>
  );
  return (
    <tr className="hover:bg-surface">
      {cella(
        <>
          <Nome v={v} className="block" />
          {/* senza nome, i primi condomìni come sul telefono: righe «non indicato» altrimenti tutte uguali */}
          {!v.amministratore && v.primi_condomini.length > 0
            && <span className="block text-xs text-muted-foreground line-clamp-2">{v.primi_condomini.join(", ")}</span>}
        </>, 0)}
      {cella(<span className="text-text">{cellaConteggio(v.condomini, v.forniture)}</span>, 1)}
      {cella(
        v.differenza_mese === null
          ? <span className="text-muted-foreground">{NESSUNA_OFFERTA_SCELTA}</span>
          : <span className={`font-semibold tabular-nums ${COLORE_TONO[tono(v.differenza_mese)]}`}>{euro(v.differenza_mese)}</span>,
        2, "text-right")}
      {cella(<span className="text-text">{dataBreve(v.modificato_il)}</span>, 3)}
      {cella(<span className={v.ultimo_invio ? "text-text" : "text-muted-foreground"}>{cellaInvio(v.ultimo_invio)}</span>, 4)}
    </tr>
  );
}

/** I preventivi salvati: schede sul telefono, tabella da 768 px, e gli stati senza righe (caricamento, guasto, vuota, nessun risultato). */
export function ListaPreventivi({ voci, q, errore, onRiprova, href, spenta, onApri, schede, azioneVuota }: Props) {
  const largo = useSchermoLargo() && !schede;
  if (voci === null) {
    return errore
      ? <div className="rounded-lg border border-border bg-card p-3"><AvvisoErrore testo={COLLEGAMENTO_NON_RIUSCITO} onRiprova={onRiprova} /></div>
      : <div aria-busy="true" className="h-24 rounded-lg border border-border bg-card animate-pulse motion-reduce:animate-none" />;
  }
  if (voci.length === 0) {
    return q ? (
      <div className="space-y-1">
        <p role="status" className="text-sm font-medium text-text">{nessunAmministratore(q)}</p>
        <p className="text-sm text-muted-foreground">{SENZA_NOME_IN_LISTA}</p>
      </div>
    ) : (
      <div className="rounded-lg border border-dashed border-border bg-card p-6 text-center">
        <p className="font-medium text-text">{NESSUN_PREVENTIVO}</p>
        {azioneVuota && <div className="mt-4">{azioneVuota}</div>}
      </div>
    );
  }
  if (largo) {
    return (
      // con la barra laterale restano circa 500 px per cinque colonne: la tabella scorre dentro il suo riquadro, non taglia l'ultima
      <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full min-w-[34rem] bg-card text-sm">
        <thead className="bg-surface text-left text-muted-foreground">
          <tr>
            <th scope="col" className={TH}>{COL_AMMINISTRATORE}</th>
            <th scope="col" className={TH}>{COL_CONDOMINI}</th>
            <th scope="col" className={`${TH} text-right`}>{COL_DIFFERENZA}</th>
            <th scope="col" className={TH}>{COL_MODIFICATO}</th>
            <th scope="col" className={TH}>{COL_ULTIMO_INVIO}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {voci.map((v) => <Riga key={v.id} v={v} href={href(v)} onApri={onApri} />)}
        </tbody>
      </table>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
      {voci.map((v) => <Scheda key={v.id} v={v} href={href(v)} frase={spenta?.(v)} onApri={onApri} />)}
    </ul>
  );
}
